/* nplus-leasing-deposit.js — <nplus-leasing-deposit> element.
   cfg.requirePayment (from block.settings.require_deposit_payment) selects the flow:
     true  — combined leasing inquiry + card deposit: one submit creates a Shopify draft
             order and a Stripe PaymentIntent (cross-linked) via the backend, then
             confirms the payment. Uses Stripe.js (global Stripe).
     false — no-payment leasing/financing inquiry: one submit creates the draft order
             (+ customer, + HubSpot sync) with no Stripe involved.
   Loaded after nplus-leasing-deposit-service.js (window.nplusLeasingDeposit). */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var BILLING_REQUIRED = ['address1', 'city', 'province', 'zip', 'country'];
  // Lease provider mode: eligibility key format per provider key type. Codes are
  // case-sensitive alphanumerics (N38Drk, DFGMPo), not numbers.
  var KEY_FORMATS = { email: EMAIL_RE, order_code: /^[A-Za-z0-9]{6}$/, token: /^[A-Za-z0-9]{6}$/ };
  // Private-mail domains: suggests the customer entered a personal address rather than
  // the one registered with the provider. Soft warning only, never blocks submit.
  var FREEMAIL_RE = /@(gmail\.com|googlemail\.com|gmx\.[a-z]+|web\.de|outlook\.[a-z]+|hotmail\.[a-z.]+|live\.[a-z.]+|yahoo\.[a-z.]+|t-online\.de|icloud\.com|me\.com)$/i;

  // Stripe.js error ({ message }, already localised for the customer) → Error flagged for showSubmitError.
  function stripeError(e) {
    var err = new Error((e && e.message) || '');
    err.stripe = true;
    return err;
  }

  class NplusLeasingDeposit extends HTMLElement {
    connectedCallback() {
      if (this._connected) return; // guard against duplicate listeners on DOM re-insertion
      this._connected = true;
      var cfgEl = this.querySelector('.nplus-leasing-deposit__config');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }

      var self = this;
      this.fields = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-field]'), function (el) {
        self.fields[el.getAttribute('data-nplus-field')] = el;
      });
      this.billing = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-billing]'), function (el) {
        self.billing[el.getAttribute('data-nplus-billing')] = el;
      });
      this.protection = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-protection]'), function (el) {
        self.protection[el.getAttribute('data-nplus-protection')] = el;
      });
      this.consentEl = this.querySelector('[data-nplus-consent]');
      var label = this.querySelector('[data-nplus-consent-label]');
      if (label) label.textContent = this.cfg.consentLabel || 'I consent.';
      this.termsEl = this.querySelector('[data-nplus-terms]');
      this.reservationRowEl = this.querySelector('[data-nplus-reservation-row]');
      this.submitEl = this.querySelector('[data-nplus-submit]');
      this.statusEl = this.querySelector('[data-nplus-status]');

      if (this.submitEl) this.submitEl.addEventListener('click', this.onSubmit.bind(this));

      // Lease provider mode (nplus-leasing-deposit-form.liquid): the provider select gates
      // the rest of the form and decides which eligibility key is asked for.
      this.providerEl = this.querySelector('[data-nplus-provider]');
      this.gatedEl = this.querySelector('[data-nplus-provider-gated]');
      this.keyEl = this.querySelector('[data-nplus-field="eligibilityKey"]');
      this.keyMissingEl = this.querySelector('[data-nplus-key-missing]');
      this.keyMissingRowEl = this.querySelector('[data-nplus-key-missing-row]');
      this.keyWarnEl = this.querySelector('[data-nplus-key-warning]');
      if (this.providerEl) this.providerEl.addEventListener('change', this.onProviderChange.bind(this));
      if (this.keyMissingEl) this.keyMissingEl.addEventListener('change', this.onKeyMissingChange.bind(this));
      if (this.keyEl) this.keyEl.addEventListener('blur', this.checkKeyDomain.bind(this));

      // Country of employment (provider mode) picks the route — see route(). Liquid renders
      // the visitor country's route; re-applied here in case the browser restored another value.
      this.employmentEl = this.querySelector('[data-nplus-employment-country]');
      this.partnerConsentEl = this.querySelector('[data-nplus-partner-consent]');
      var leasePanel = this.closest('[data-nplus-panel]');
      this.introEl = leasePanel ? leasePanel.querySelector('[data-nplus-lease-intro]') : null;
      if (this.employmentEl) {
        this.employmentEl.addEventListener('change', this.applyRoute.bind(this));
        this.applyRoute();
      }
      // Belgian partner-share consent: remember when it was ticked (the backend stamps its own time too).
      if (this.partnerConsentEl) this.partnerConsentEl.addEventListener('change', function () {
        self._partnerConsentAt = self.partnerConsentEl.checked ? new Date().toISOString() : null;
      });

      // Submit the size picked in the buy-box pills, not the one the page loaded with
      // (cfg.variantId is rendered once by Liquid; nplus-buybox.js emits pill changes).
      document.addEventListener('nplus:bike-variant-change', function (e) {
        var id = e.detail && parseInt(e.detail.variantId, 10);
        if (id) self.cfg.variantId = id;
        self.applyStockState();
      });
      this.applyStockState();

      // Mount the Payment Element when our reveal panel opens. The buybox host only
      // auto-mounts its own 'deposit' panel; setTimeout defers past togglePanel's unhide.
      var host = this.closest('nplus-buybox');
      var reveal = host ? host.querySelector('[data-nplus-reveal="leasing-deposit"]') : null;
      if (reveal) reveal.addEventListener('click', function () { setTimeout(function () { self.ensureStripe(); }, 0); });
    }

    setStatus(msg, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg || '';
      this.statusEl.hidden = !msg;
      this.statusEl.classList.toggle('nplus-bb__status--error', !!isError);
    }
    setBusy(b) { if (this.submitEl) { this.submitEl.disabled = b; this.submitEl.setAttribute('aria-busy', b ? 'true' : 'false'); } }
    t(key, fallback) { return (this.cfg && this.cfg.i18n && this.cfg.i18n[key]) || fallback; }

    showFieldError(key, msg) {
      var el = this.querySelector('[data-nplus-error-for="' + key + '"]');
      if (el) { el.textContent = msg; el.hidden = false; }
    }
    clearFieldErrors() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-error-for]'), function (e) { e.hidden = true; });
    }

    // Pre-order bikes: the Reservation Agreement applies only to a size without stock on hand
    // (config inStock, inventory_quantity > 0); a size in stock ships now, so the row is hidden and
    // not required.
    applyStockState() {
      if (!this.reservationRowEl) return;
      this.reservationRowEl.hidden = !!(this.cfg.inStock && this.cfg.inStock[this.cfg.variantId]);
    }
    reservationHidden() { return !!(this.reservationRowEl && this.reservationRowEl.hidden); }

    // 'de' | 'be' from the Country of employment select (Germany, plus Belgium while the theme
    // setting nplus_be_partner_name names the partner dealership = cfg.bePartnerName); 'de'
    // without the select (legacy markup) or for anything that isn't a named-dealer Belgium.
    route() {
      if (!this.employmentEl) return 'de';
      if (this.employmentEl.value === 'BE' && this.cfg.bePartnerName) return 'be';
      return 'de';
    }

    // Shows only the current route's [data-nplus-route] parts. The German route keeps its
    // provider gate; Belgium has no provider to pick, so the contact
    // fields show straight away. evt = a user change (not the initial call from connectedCallback).
    applyRoute(evt) {
      var route = this.route();
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-route]'), function (el) {
        el.hidden = el.getAttribute('data-nplus-route') !== route;
      });
      if (this.gatedEl) this.gatedEl.hidden = route === 'de' ? !this.keyType() : false;
      if (this.introEl) this.introEl.hidden = route !== 'de';
      if (route !== 'be' && this.partnerConsentEl && this.partnerConsentEl.checked) { this.partnerConsentEl.checked = false; this._partnerConsentAt = null; }
      if (evt) { this.clearFieldErrors(); this.ensureStripe(); }
    }

    keyType() {
      var opt = this.providerEl && this.providerEl.options[this.providerEl.selectedIndex];
      return (opt && opt.getAttribute('data-key-type')) || '';
    }

    onProviderChange() {
      var type = this.keyType();
      if (this.gatedEl) this.gatedEl.hidden = !type;
      this.clearFieldErrors();
      if (!type) return;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-key-for]'), function (el) {
        el.hidden = el.getAttribute('data-nplus-key-for') !== type;
      });
      var provider = this.providerEl.value;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-order-code-provider]'), function (el) {
        el.hidden = el.getAttribute('data-nplus-order-code-provider') !== provider;
      });
      if (this.keyEl) {
        this.keyEl.value = ''; // a key typed for one provider is never valid for another
        this.keyEl.type = type === 'email' ? 'email' : 'text';
        this.keyEl.autocomplete = type === 'email' ? 'email' : 'off';
        if (type === 'email') this.keyEl.removeAttribute('maxlength'); else this.keyEl.maxLength = this.providerEl.value === 'linexo' ? 40 : 6; // linexo codes vary in length
      }
      if (this.keyWarnEl) this.keyWarnEl.hidden = true;
      // No "don't have it" box for the optional company email; untick it on every switch so a
      // box ticked for another provider can't leave this key field disabled.
      if (this.keyMissingRowEl) this.keyMissingRowEl.hidden = type === 'email';
      if (this.keyMissingEl && this.keyMissingEl.checked) { this.keyMissingEl.checked = false; this.onKeyMissingChange(); }
      this.ensureStripe(); // held back until now, see ensureStripe()
    }

    onKeyMissingChange() {
      var missing = this.keyMissingEl.checked;
      if (this.keyEl) { this.keyEl.disabled = missing; if (missing) this.keyEl.value = ''; }
      if (missing && this.keyWarnEl) this.keyWarnEl.hidden = true;
    }

    checkKeyDomain() {
      if (!this.keyWarnEl || !this.keyEl) return;
      var v = this.keyEl.value.trim();
      this.keyWarnEl.hidden = !(this.keyType() === 'email' && EMAIL_RE.test(v) && FREEMAIL_RE.test(v));
    }

    ensureStripe() {
      if (!this.cfg.requirePayment) return; // no-payment inquiry flow — nothing to mount
      // Provider mode: the Payment Element target sits inside the gated block, so hold the
      // mount until a provider is chosen (onProviderChange calls back in). The reveal's call
      // from nplus-buybox.js togglePanel lands here first and returns without latching.
      if (this.cfg.providerMode && this.route() === 'de' && !this.keyType()) return;
      if (this._stripeInit) return;
      this._stripeInit = true;
      if (typeof window.Stripe !== 'function' || !window.nplusLeasingDeposit || !this.cfg.publishableKey) {
        this.setStatus(this.t('payment_unavailable', 'Payment is temporarily unavailable.'), true);
        if (this.submitEl) this.submitEl.disabled = true;
        return;
      }
      this.stripe = window.Stripe(this.cfg.publishableKey);
      this.elements = this.stripe.elements({ mode: 'payment', amount: Math.round(Number(this.cfg.amount)), currency: String(this.cfg.currency || '').toLowerCase(), paymentMethodTypes: ['card'], appearance: { theme: 'stripe' } });
      this.paymentElement = this.elements.create('payment');
      // Mount into THIS element's own target — the Lease and Finance tabs each render an
      // <nplus-leasing-deposit>, so a global #id would collide. Falls back to the legacy id.
      this.paymentElement.mount(this.querySelector('[data-nplus-pe]') || '#nplus-payment-element');
      this.cardReady = false;
      var self = this;
      this.paymentElement.on('change', function (e) { self.cardReady = !!e.complete; });
    }

    collect() {
      var f = this.fields, b = this.billing, p = this.protection;
      var val = function (el) { return el ? el.value.trim() : ''; };
      var out = {
        productId: this.cfg.productId,
        variantId: this.cfg.variantId,
        type: this.cfg.type || 'leasing',
        // Admin-facing tag distinguishing the Lease vs Finance tab. Only the
        // no-payment /create-leasing-inquiry route reads `tag`; the deposit route
        // ignores it, so sending it unconditionally is harmless either way.
        tag: this.cfg.type === 'finance' ? 'financing-inquiry' : 'leasing-inquiry',
        customer: { firstName: val(f.firstName), lastName: val(f.lastName), email: val(f.email), phone: val(f.phone) },
        employer: val(f.employer),
        consent: !!(this.consentEl && this.consentEl.checked),
        currency: this.cfg.presentmentCurrency,
        locale: this.cfg.locale,
        accessories: (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
          ? window.nplusAccessories.getSelection() : []
      };
      // Landing-page lightbox only (CR-44): backend adds it as a draft tag + "Source:" note line.
      if (this.cfg.source) out.source = this.cfg.source;
      // Product page opened from a landing page's product link: also a landing-page lead. Lease only.
      else if (out.type !== 'finance' && window.nplusLeasingDeposit && window.nplusLeasingDeposit.landingLeadSource) {
        var landing = window.nplusLeasingDeposit.landingLeadSource();
        if (landing) out.source = landing;
      }
      if (this.cfg.showBilling) {
        out.billing = { address1: val(b.address1), address2: val(b.address2), city: val(b.city), province: val(b.province), zip: val(b.zip), country: val(b.country) };
      }
      // Provider mode doesn't render the theft/wear/inspection selects (cover preference
      // replaces them), so don't send an all-empty protection object.
      if (this.cfg.showProtection && !this.cfg.providerMode) {
        out.protection = { theft: val(p.theft), wear: val(p.wear), inspection: val(p.inspection) };
      }
      // Shape matches the backend's lease object (nplus-deposit-app lib/leasing.js normalizeLease).
      // Employment country is kept apart from billing.country, which stays the customer's
      // residence. The German route's body is exactly as before (same six keys, 'DE').
      if (this.cfg.providerMode && this.route() !== 'de') {
        // Belgium: no German provider or key. The backend derives the draft's route tag
        // (leasing-route-be) from employment_country.
        out.lease = { provider: 'other', key_type: '', key: '', key_supplied: false, cover_preference: null, employment_country: this.employmentEl.value };
        if (this.route() === 'be') {
          var consented = !!(this.partnerConsentEl && this.partnerConsentEl.checked);
          out.lease.consent_partner_share = consented;
          if (consented) out.lease.consent_partner_share_at = this._partnerConsentAt || new Date().toISOString();
          out.lease.partner_name = this.cfg.bePartnerName || '';
        }
      } else if (this.cfg.providerMode) {
        var keyMissing = !!(this.keyMissingEl && this.keyMissingEl.checked);
        var key = keyMissing ? '' : val(f.eligibilityKey);
        // Company email and Lease a Bike order code are optional: blank = not supplied. The
        // Eurorad token stays required unless its box is ticked (validate() flags it).
        var keyOptional = this.keyType() === 'email' || this.keyType() === 'order_code';
        var provider = this.providerEl ? this.providerEl.value : '';
        out.lease = {
          provider: provider,
          key_type: this.keyType(),
          key: key,
          key_supplied: !keyMissing && !(keyOptional && !key),
          cover_preference: null, // question removed Oct 2026 (confusing); key kept for the backend
          employment_country: 'DE'
        };
      }
      return out;
    }

    validate(payload) {
      this.clearFieldErrors();
      if (this.cfg.providerMode && this.route() === 'de' && !payload.lease.provider) {
        this.showFieldError('provider', this.t('required', 'Required'));
        return false; // nothing below the provider select is visible yet
      }
      var ok = true;
      var req = function (cond, key, msg) { if (cond) { ok = false; this.showFieldError(key, msg || this.t('required', 'Required')); } }.bind(this);
      var c = payload.customer;
      req(!c.firstName, 'firstName'); req(!c.lastName, 'lastName');
      if (!c.email) req(true, 'email'); else if (!EMAIL_RE.test(c.email)) { this.showFieldError('email', this.t('invalid_email', 'Enter a valid email')); ok = false; }
      req(!payload.employer, 'employer');
      if (this.cfg.showBilling) {
        BILLING_REQUIRED.forEach(function (k) { req(!payload.billing[k], k); });
      }
      if (this.cfg.providerMode) {
        var l = payload.lease;
        if (l.key_supplied) {
          if (!l.key) req(true, 'eligibilityKey');
          else if (l.provider !== 'linexo' && !KEY_FORMATS[l.key_type].test(l.key)) { // linexo: any code accepted (format varies)
            this.showFieldError('eligibilityKey', l.key_type === 'email' ? this.t('invalid_email', 'Enter a valid email') : this.t('invalid_key', 'Enter the 6-character code.'));
            ok = false;
          }
        }
        if (this.route() === 'be' && !l.consent_partner_share) {
          this.showFieldError('partnerConsent', this.t('partner_consent_required', 'Please confirm we may share your details with our partner dealership.'));
          ok = false;
        }
      }
      if (!payload.consent) { this.showFieldError('consent', this.t('consent_required', 'Consent is required to submit.')); ok = false; }
      return ok;
    }

    onSubmit() {
      this.setStatus('');
      if (!window.nplusLeasingDeposit) { this.setStatus('Service unavailable. Please refresh.', true); return; }
      var payload = this.collect();
      if (!this.validate(payload)) return;
      // Terms checkbox exists only for the Reservation Agreement (pre-order products).
      if (this.termsEl && !this.reservationHidden() && !this.termsEl.checked) { this.setStatus(this.t('accept_terms', 'Please accept the deposit terms.'), true); return; }

      var self = this;

      if (!this.cfg.requirePayment) {
        this.setBusy(true);
        window.nplusLeasingDeposit.createLeasingInquiry(payload)
          .then(function () { self.trackEnquiry(payload); self.showSuccess(); })
          .catch(function (err) {
            self.showSubmitError(err);
            self.setBusy(false);
          });
        return;
      }

      this.ensureStripe();
      if (!this.stripe || !this.elements) { this.setStatus('Payment is not ready. Please refresh.', true); return; }
      if (!this.cardReady) { this.setStatus(this.t('card_incomplete', 'Please complete your card details.'), true); return; }

      this.setBusy(true);
      window.nplusLeasingDeposit.createInquiryWithDeposit(payload).then(function (res) {
        return self.elements.submit().then(function (sub) {
          if (sub && sub.error) throw stripeError(sub.error);
          return self.stripe.confirmPayment({
            elements: self.elements,
            clientSecret: res.clientSecret,
            confirmParams: { return_url: self.confirmationUrl() },
            redirect: 'if_required'
          });
        });
      }).then(function (result) {
        if (result && result.error) throw stripeError(result.error);
        var pi = result && result.paymentIntent;
        self.trackEnquiry(payload); // deposit confirmed (a 3-D Secure redirect leaves the page before this)
        if (pi && pi.id) { window.location.assign(self.confirmationUrl() + '?payment_intent=' + encodeURIComponent(pi.id)); return; }
        self.showSuccess();
      }).catch(function (err) {
        self.showSubmitError(err);
        self.setBusy(false);
      });
    }

    // Submit failures never show backend or parser text. A rejected email (backend 422
    // invalid_email) is a field error under the email input; a Stripe card error keeps Stripe's
    // own customer-facing, localised message; anything else is the translated generic message.
    showSubmitError(err) {
      if (err && err.code === 'invalid_email') {
        this.setStatus('');
        this.showFieldError('email', this.t('email_rejected', 'Please check your email address.'));
        if (this.fields.email && typeof this.fields.email.focus === 'function') this.fields.email.focus();
        return;
      }
      if (err && err.stripe && err.message) { this.setStatus(err.message, true); return; }
      this.setStatus(this.t('submit_failed', 'Something went wrong. Please try again.'), true);
    }

    // GTM conversion event after a confirmed successful submit — never on validation errors or
    // failures, once per submission. No personal data: only where the form is, the lease provider
    // and route (lease enquiries), the product and size, and the size's price. value is in EUR
    // (major units); null when the visitor's currency isn't EUR rather than a mislabelled amount.
    trackEnquiry(payload) {
      if (this._enquiryTracked) return;
      this._enquiryTracked = true;
      var lease = this.cfg.type !== 'finance';
      var location;
      if (this.cfg.source === 'lease-source-landing') location = 'landing_lease_lightbox';
      else if (lease) location = 'pdp_lease_tab';
      else {
        // Germany + payever: this Finance form is the "Nicht in Deutschland?" fallback under the payever CTA.
        var tab = this.closest('[data-nplus-tabpanel="finance"]');
        location = tab && tab.querySelector('[data-method="payever"]') ? 'pdp_finance_fallback' : 'pdp_finance_tab';
      }
      var variantId = Number(this.cfg.variantId) || null;
      var minor = variantId != null && this.cfg.variantPrices ? this.cfg.variantPrices[variantId] : null;
      var l = lease && payload && payload.lease;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: lease ? 'lease_enquiry_submitted' : 'finance_enquiry_submitted',
        form_location: location,
        provider: (l && l.provider) || null,
        lease_route: l && this.cfg.providerMode ? this.route() : null,
        product_handle: this.cfg.productHandle || null,
        variant_id: variantId,
        value: this.cfg.presentmentCurrency === 'EUR' && typeof minor === 'number' ? minor / 100 : null,
        currency: 'EUR'
      });
    }

    // Absolute URL of the deposit confirmation page, locale-prefixed (e.g. /de/pages/…).
    confirmationUrl() {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return window.location.origin + root + 'pages/deposit-confirmation';
    }

    showSuccess() {
      var title = this.t('success_title', 'Inquiry & deposit received');
      var body = this.t('success_body', 'Thank you — your deposit is confirmed and your leasing inquiry is in. Our team will be in touch shortly.');
      this.innerHTML = '<div class="nplus-leasing__success" tabindex="-1"><h3>' + title + '</h3><p>' + body + '</p></div>';
      this.revealSuccess(this.firstElementChild);
    }

    // The form collapses to the message above, which can leave it out of view (looks like the
    // submit failed). Bring it into view and move focus to it for screen readers; preventScroll:
    // the scroll is ours (smooth, unless reduced motion is preferred).
    //  - Landing-page lightbox (dialog.nplus-lease-lightbox, sections/nplus-sticky-lease-cta.liquid):
    //    the dialog's panel is the scroll container, so centre the message in the panel. The page
    //    behind the modal is scroll-locked and inert — it is never scrolled.
    //  - Product-page buy box: centre it in the window, kept clear of the jump nav when that would
    //    be pinned at the target scroll position.
    revealSuccess(el) {
      if (!el) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var behavior = reduce ? 'auto' : 'smooth';
      var focus = function () { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } };
      var rect = el.getBoundingClientRect();

      var dialog = this.closest('dialog.nplus-lease-lightbox');
      var panel = dialog && dialog.querySelector('.nplus-lease-lightbox__panel');
      if (panel) {
        var panelTop = panel.scrollTop + (rect.top - panel.getBoundingClientRect().top);
        var target = Math.max(0, Math.round(panelTop - Math.max(0, (panel.clientHeight - rect.height) / 2)));
        if (typeof panel.scrollTo === 'function') panel.scrollTo({ top: target, behavior: behavior });
        else panel.scrollTop = target;
        focus();
        return;
      }

      var docTop = rect.top + window.pageYOffset;
      var top = docTop - Math.max(0, (window.innerHeight - rect.height) / 2);
      // sections/jump-nav.liquid: its bar goes position:fixed at the top once scrolled past the section.
      var nav = document.querySelector('[x-data="jumpNav()"]');
      var bar = nav && nav.firstElementChild;
      if (bar && top >= nav.getBoundingClientRect().top + window.pageYOffset) {
        top = Math.min(top, docTop - bar.offsetHeight - 16);
      }
      window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: behavior });
      focus();
    }
  }

  if (!customElements.get('nplus-leasing-deposit')) {
    customElements.define('nplus-leasing-deposit', NplusLeasingDeposit);
  }
})();

